<script lang="ts" setup>
import QrcodeVue from 'qrcode.vue';
import {onMounted, ref, Ref, watch} from 'vue';
import {VueRecaptcha} from 'vue-recaptcha';
import {analyticsInit, trackEvent} from '../../components/app/analytics';
import {AccountSettingsService} from '../../components/settings/account-settings/account-settings.service';
import {changeLanguage, getStrings} from '../../core/lang';
import {useService} from '../../core/useService';
import {ChangePasswordRecord} from '../../records/change-password.record';
import {LoginRecord} from '../../records/login.record';
import {UISettingsRecord} from '../../records/ui-settings.record';
import {AuthResponse, AuthService, SsoProvider} from '../auth.service';
import {UserRecord} from '../../records/user.record';
import strings from '../auth.strings.yaml';
import OneTimePassword from './OneTimePassword.vue';
import MXTour from '../../components/tour/MXTour.vue';
import {IconAccessIdentity} from '../../components/icons';
import EvoMigrationPrompt from './EvoMigrationPrompt.vue';

const S = ref<AuthStrings>(getStrings<AuthStrings>(strings));
const authService = useService<AuthService>(AuthService);

const props = defineProps<{
    account: string;
}>();
const emit = defineEmits(['success', 'ready']);

/** Chrome/WebKit autofill background; inner shadow + text fill restore white + brand text */
const autofillInputFix =
    '[&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#fff] [&:-webkit-autofill]:[-webkit-text-fill-color:#1b1c41]';

enum LoginAction {
    Idle,
    Login,
    Enroll2FA,
    Enroll2FAComplete,
    Request2FA,
    ForgotPassword,
    ForgotPasswordSent,
    ResetPassword,
    Using2FA,
    EvoMigration
}
const showLogin: Ref<boolean> = ref(false);
const action: Ref<LoginAction> = ref();
const loginRecord: Ref<LoginRecord> = ref(new LoginRecord());
const passwordRecord: Ref<ChangePasswordRecord> = ref(null);
const authResponse: Ref<AuthResponse> = ref(null);
const secret: Ref<string> = ref(null);
const loading: Ref<boolean> = ref(false);
const uiSettings = ref<UISettingsRecord>(null);
const oauthError: Ref<string | null> = ref(null);
const oauthRedirecting: Ref<boolean> = ref(false);
const currentUser: Ref<UserRecord | null> = ref(null);

loginRecord.value.account = props.account;

// load ui settings
authService.uiSettings(loginRecord.value.account).then((settings: UISettingsRecord) => {
    analyticsInit(settings);
    uiSettings.value = settings;
    changeLanguage(settings.language);
    changeTitle(settings.title);
    S.value = getStrings<AuthStrings>(strings);
});

function changeTitle(bkVersion: string) {
    if (!IS_DEBUG) {
        document.title = `${bkVersion} [ui-version: ${window.ASSETS_URL.replace(
            /https:\/\/assets\.maxoptra\.com\/(.+?)\/$/,
            '$1'
        )}]`;
    }
}

function skipEvoMigration() {
    sessionStorage.setItem('evo_migration_skipped', 'true');
    closeLogin();
}

function closeLogin() {
    showLogin.value = false;
    action.value = LoginAction.Login;
    emit('success');
}

function isNeedToShowEvoMigration() {
    if (currentUser.value?.evoUserId) {
        return false;
    }
    const skippedFlag =
        sessionStorage.getItem('evo_migration_skipped') === 'true' && !uiSettings.value.enforceEvoMigration;
    return uiSettings.value.evoLinked && !skippedFlag;
}

function handleEvoMigrationError() {
    console.error('Evo migration error');
}

onMounted(async () => {
    // Parse OAuth error from query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');
    const ssoProvider = urlParams.get('ssoProvider');
    if (errorParam) {
        oauthError.value = errorParam;
        // Clear error parameter from URL without reloading
        window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
    }

    // If running inside an iframe and a prompt=none failure was returned, ask the platform
    // to re-authenticate the user instead of showing the login form.
    if (oauthError.value && window.self !== window.top) {
        window.parent.postMessage(JSON.stringify({type: 'RequestReauthentication', url: '/'}), '*');
        return;
    }

    // Check if we just returned from OAuth2 flow
    const justReturnedFromOAuth = sessionStorage.getItem('oauth2InProgress');
    if (justReturnedFromOAuth) {
        // Clear flag - we just came back from OAuth2, don't redirect again
        sessionStorage.removeItem('oauth2InProgress');
    }

    // Check if user is already logged in first
    const loggedIn = await authService.isLoggedIn();

    // Auto-redirect to Access Identity only when ssoProvider=AccessIdentity is present
    if (
        ssoProvider === SsoProvider.AccessIdentity &&
        !loggedIn &&
        !oauthError.value &&
        !errorParam &&
        !justReturnedFromOAuth
    ) {
        const hash = document.location.hash.split('hash=')[1];
        // Only auto-redirect if not resetting password
        if (!hash) {
            // Set flag to prevent redirect loop
            sessionStorage.setItem('oauth2InProgress', 'true');
            oauthRedirecting.value = true;
            await loginWithAccessIdentity();
            return;
        }
    }

    // Normal login check flow
    checkLogin().then(() => {
        emit('ready', showLogin.value);
    });
});

watch(
    () => loginRecord.value.code,
    async (code) => {
        // If the user has entered an OTP
        if (code.length === 6) {
            if (action.value === LoginAction.Enroll2FA) {
                // if it is the first time setting up 2FA
                loading.value = true;
                authResponse.value = await authService.activate2FA(code);
                if (!authResponse.value.errors?.length) {
                    // // if code was correct
                    // await checkLogin();
                    // loginRecord.value.code = '';
                    action.value = LoginAction.Enroll2FAComplete;
                    const settings = await useService(AccountSettingsService).getAppSettings();
                    settings.twoFactorAuthActive = true;
                    settings.twoFactorAuthEnabled = true;
                }
                loading.value = false;
            } else if (action.value === LoginAction.Using2FA) {
                // if 2fa is enabled for this user then just login
                await login();
            }
        }
    }
);

/**
 * Check if the user is already logged in and redirect to default page if they are
 */
async function checkLogin() {
    const hash = document.location.hash.split('hash=')[1];
    // Reset Password
    if (hash) {
        passwordRecord.value = new ChangePasswordRecord();
        passwordRecord.value.accountUrl = props.account;
        passwordRecord.value.hash = hash;
        action.value = LoginAction.ResetPassword;
        await authService.getLoginByLink(hash).then((response) => {
            if (response?.errors && response?.errors.length) {
                authResponse.value = response;
            } else {
                passwordRecord.value.login = response.rows[0];
                authResponse.value = null;
            }
        });
        showLogin.value = true;
    } else {
        const loggedIn = await authService.isLoggedIn();
        if (loggedIn) {
            loading.value = true;
            currentUser.value = await authService.loadUser();
            const {twoFactorAuthActive, twoFactorAuthEnabled} =
                await useService(AccountSettingsService).getAppSettings();
            if (twoFactorAuthActive && !twoFactorAuthEnabled && !sessionStorage.getItem('skip2FA')) {
                // if 2fa is active for account and not enabled for this user
                action.value = LoginAction.Request2FA;
                loading.value = false;
                showLogin.value = true;
            } else {
                // if 2fa is not active for account
                if (isNeedToShowEvoMigration()) {
                    loading.value = false;
                    action.value = LoginAction.EvoMigration;
                    showLogin.value = true;
                } else {
                    closeLogin();
                }
            }
            if (twoFactorAuthActive && twoFactorAuthEnabled) {
                trackEvent('action', 'login-2fa', null);
            } else {
                trackEvent('action', 'login', null);
            }
            authService.keepAlive();
        } else {
            sessionStorage.removeItem('evo_migration_skipped');
            action.value = LoginAction.Login;
            showLogin.value = true;
        }
    }
}

/**
 * Setting up 2FA
 */
async function setup2FA() {
    // Request a secret from the server
    loading.value = true;
    secret.value = await authService.get2FASecret();
    loading.value = false;
    action.value = LoginAction.Enroll2FA;
}

function skip2FA() {
    if (isNeedToShowEvoMigration()) {
        loading.value = false;
        action.value = LoginAction.EvoMigration;
        showLogin.value = true;
    } else {
        closeLogin();
    }
    sessionStorage.setItem('skip2FA', '1');
    trackEvent('action', '2fa-enrol-skip', null);
}

/**
 * Check that login and password are valid and captcha is valid if required
 */
function isLoginAndPasswordValid(): boolean {
    return (
        loginRecord.value.login?.length > 0 &&
        loginRecord.value.password?.length > 0 &&
        (!authResponse.value?.showCaptcha || loginRecord.value.googleRecaptchaResponse?.length > 0)
    );
}

/**
 * Login the user
 */
async function login() {
    if (isLoginAndPasswordValid()) {
        loading.value = true;
        authResponse.value = await authService.login(loginRecord.value);
        if (authResponse.value?.errors?.length) {
            if (action.value === LoginAction.Using2FA) {
                loginRecord.value.code = '';
                // action.value = LoginAction.Login;
            }
            loading.value = false;
        } else if (authResponse.value?.required2FA) {
            // if 2fa is required for this user
            action.value = LoginAction.Using2FA;
            loading.value = false;
        } else {
            // if 2fa is not required for this user
            await checkLogin();
            // loading.value = false;
        }
    }
}

/**
 * Login with Access Identity SSO
 */
async function loginWithAccessIdentity() {
    loading.value = true;
    // Set flag to prevent redirect loop when returning from OAuth2
    sessionStorage.setItem('oauth2InProgress', 'true');
    await authService.loginWithAccessIdentity(loginRecord.value.account);
}

/**
 * Reset the password
 */
async function resetPassword() {
    if (loginRecord.value.login?.length > 0) {
        // send the email
        loading.value = true;
        authResponse.value = await authService.rememberPassword(loginRecord.value);
        loading.value = false;
        if (!authResponse.value.errors?.length) {
            // if no errors, show the success message
            action.value = LoginAction.ForgotPasswordSent;
            trackEvent('action', 'forgot-password', null);
        }
    }
}
async function changePassword() {
    authResponse.value = await authService.resetPassword(passwordRecord.value).then((result) => {
        if (result?.success) {
            emit('success');
            showLogin.value = false;
            trackEvent('action', 'password-change', null);
        } else {
            return result;
        }
    });
}

function reload() {
    window.location.reload();
}
/**
 * Recaptcha callback
 */
function onVerify(recaptchaToken: string) {
    loginRecord.value.googleRecaptchaResponse = recaptchaToken;
}

/**
 * Format 2FA secret to be displayed
 */
function format2FASecret(secret: string): string {
    return secret.replace(/(.{4})/g, '$1 ').trim();
}
</script>
<template>
    <div v-if="showLogin" class="relative text-base text-[#1b1c41]">
        <MXTour context="login-component" :disabled="false" v-show="showLogin" />
        <!-- Loader -->
        <div v-if="loading" class="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/[0.97]">
            <div
                class="h-[60px] w-[60px] shrink-0 animate-spin rounded-full border-[5px] border-solid border-[#1b1c41] border-t-white"
            />
            <p class="mt-12 w-full text-center text-base font-medium text-[#1b1c41]/60">{{ S.pleaseWait }}</p>
        </div>

        <!-- OAuth Redirect Loader -->
        <div
            v-if="oauthRedirecting"
            class="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/[0.97]"
        >
            <div
                class="h-[60px] w-[60px] shrink-0 animate-spin rounded-full border-[5px] border-solid border-[#1b1c41] border-t-white"
            />
            <p class="mt-12 w-full text-center text-base font-medium text-[#1b1c41]/60">
                {{ S.redirectingToAccessIdentity }}
            </p>
        </div>

        <!-- Login -->

        <div class="p-2" v-if="action == LoginAction.Login">
            <h1 class="mb-5 text-3xl font-medium">{{ S.welcomeBack }}</h1>

            <!-- OAuth Error Message -->
            <div class="my-4 block" v-if="oauthError">
                <div
                    class="block h-fit rounded bg-[#fdf1f5] px-[15px] py-1.5 leading-[30px] text-[#e47398]"
                    v-test-id="'oauth-error-message'"
                >
                    {{ S.accessIdentityFallback }}
                </div>
            </div>

            <!-- Access Identity Sign In -->
            <div class="mt-6" v-if="uiSettings.evoLinked">
                <span>{{ S.haveAccessProfile }}</span>
                <button
                    @click="loginWithAccessIdentity"
                    class="mt-2.5 box-border flex h-11 min-h-[44px] w-full cursor-pointer items-center justify-center gap-2.5 rounded border-0 bg-[#e5173f] px-3 py-2 text-base font-normal leading-3 text-white hover:bg-[#ce1538] active:bg-[#ce1538]"
                    v-test-id="'access-identity-login'"
                >
                    {{ S.accessIdentitySignIn }}
                    <IconAccessIdentity class="shrink-0 -mr-1.5 fill-white" />
                </button>
                <div class="my-5 flex items-center text-center">
                    <div class="h-px flex-1 bg-[#d5d6de]" />
                    <span class="px-[15px] text-sm text-[#777]">{{ S.orSignInWith }}</span>
                    <div class="h-px flex-1 bg-[#d5d6de]" />
                </div>
            </div>

            <input
                name="login"
                v-model="loginRecord.login"
                autofocus
                class="mt-2.5 box-border block h-11 w-full appearance-none rounded border border-solid border-[#D5D6DE] bg-white px-3 py-2 text-base leading-3 text-[#1b1c41] outline-none focus:border-[#1b1c41]"
                :class="autofillInputFix"
                type="text"
                :placeholder="S.loginPlaceholder"
                v-test-id="'login'"
            />
            <input
                name="password"
                @keyup.enter="login"
                v-model="loginRecord.password"
                class="mt-2.5 box-border block h-11 w-full appearance-none rounded border border-solid border-[#D5D6DE] bg-white px-3 py-2 text-base leading-3 text-[#1b1c41] outline-none focus:border-[#1b1c41]"
                :class="autofillInputFix"
                type="password"
                :placeholder="S.passwordPlaceholder"
                v-test-id="'password'"
            />
            <span
                @click="action = LoginAction.ForgotPassword"
                class="mb-2.5 mt-[19px] inline-block cursor-pointer font-semibold text-[#0073ff] hover:underline"
                v-test-id="'forgot-password'"
            >
                {{ S.forgot }}
            </span>
            <div class="my-4 block" v-if="authResponse?.errors?.length">
                <span
                    class="mb-1 block h-fit rounded bg-[#fdf1f5] px-[15px] py-1.5 leading-[30px] text-[#e47398] last:mb-0"
                    v-for="error in authResponse.errors"
                    v-test-id="'login-error-message'"
                >
                    {{ S[error.message] }}
                </span>
            </div>
            <button
                @click="login"
                class="mt-2 box-border flex h-11 min-h-[44px] w-full cursor-pointer items-center justify-center rounded border-0 bg-[#1b1c41] px-3 py-2 text-base leading-3 text-white active:bg-[#252647]"
                v-test-id="'login-btn'"
            >
                {{ S.logIn }}
            </button>
            <div class="my-4 block" v-if="authResponse?.showCaptcha && uiSettings?.rcSiteKey">
                <VueRecaptcha @verify="onVerify" :sitekey="uiSettings.rcSiteKey" v-test-id="'recaptcha'" />
            </div>
        </div>

        <!-- Request 2FA -->

        <div class="p-2" v-else-if="action == LoginAction.Request2FA">
            <h1 class="mb-5 text-3xl font-medium">{{ S.twoFactorAuth }}</h1>
            <p class="mb-5">{{ S.twoFactorAuthDescription }}</p>
            <div class="flex gap-4">
                <button
                    @click="skip2FA"
                    class="box-border flex h-11 min-h-[44px] flex-1 cursor-pointer items-center justify-center rounded border border-solid border-[#1b1c41] bg-white px-3 py-2 text-base leading-3 text-[#1b1c41] active:bg-gray-100"
                    v-test-id="'skip-2fa'"
                >
                    {{ S.skip }}
                </button>
                <button
                    @click="setup2FA"
                    class="box-border flex h-11 min-h-[44px] flex-1 cursor-pointer items-center justify-center rounded border-0 bg-[#1b1c41] px-3 py-2 text-base leading-3 text-white active:bg-[#252647]"
                    v-test-id="'set-2fa'"
                >
                    {{ S.setItUp }}
                </button>
            </div>
        </div>

        <!-- Enroll2FA 2FA -->

        <div class="p-2" v-else-if="action == LoginAction.Enroll2FA">
            <h1 class="mb-5 text-3xl font-medium">{{ S.setupTwoFactorAuth }}</h1>
            <ol class="ml-4 list-decimal">
                <li class="mb-4 list-item" v-html="S.instructionLine1"></li>
                <li class="mb-4 list-item" v-html="S.instructionLine2"></li>
                <li class="mb-4 list-item" v-html="S.instructionLine3"></li>
                <QrcodeVue
                    :value="`otpauth://totp/Maxoptra:${loginRecord.login}@${loginRecord.account}?secret=${secret}&issuer=Maxoptra`"
                    :size="150"
                />
                <p class="my-4 w-52 font-mono text-lg font-semibold" v-test-id="'2fa-secret'">
                    {{ format2FASecret(secret) }}
                </p>
                <li class="mb-4 list-item" v-html="S.instructionLine4"></li>
            </ol>
            <OneTimePassword :count="6" v-model:value="loginRecord.code" />
            <div class="my-4 block" v-if="authResponse?.errors?.length">
                <span
                    class="mb-1 block h-fit rounded bg-[#fdf1f5] px-[15px] py-1.5 leading-[30px] text-[#e47398] last:mb-0"
                    v-for="error in authResponse.errors"
                    v-test-id="'enroll-error-message'"
                >
                    {{ S[error.message] }}
                </span>
            </div>
            <button
                @click="
                    authResponse = null;
                    action = LoginAction.Request2FA;
                "
                v-test-id="'login-back'"
                class="mt-8 box-border flex h-11 min-h-[44px] w-full max-w-full cursor-pointer items-center justify-center rounded border border-solid border-[#1b1c41] bg-white px-3 py-2 text-base leading-3 text-[#1b1c41] active:bg-gray-100 sm:inline-flex sm:w-auto"
            >
                {{ S.back }}
            </button>
        </div>

        <!-- Enroll 2FA complete -->

        <div class="p-2" v-else-if="action == LoginAction.Enroll2FAComplete">
            <h1 class="mb-5 text-3xl font-medium">{{ S.congrats }}</h1>
            <p class="mb-5">{{ S.twoFactorAuthEnabled }}</p>
            <button
                @click="checkLogin()"
                class="mx-auto box-border flex h-11 min-h-[44px] w-auto min-w-[8rem] cursor-pointer items-center justify-center rounded border-0 bg-[#1b1c41] px-3 py-2 text-base leading-3 text-white active:bg-[#252647]"
                v-test-id="'2fa-continue'"
            >
                {{ S.continue }}
            </button>
        </div>

        <!-- Using 2FA -->

        <div class="p-2" v-else-if="action == LoginAction.Using2FA">
            <h1 class="mb-5 text-3xl font-medium">{{ S.twoFactorAuth }}</h1>
            <p class="mb-5 leading-[25px] text-[#1b1c41]">{{ S.instructionLine4 }}</p>
            <OneTimePassword :count="6" v-model:value="loginRecord.code" />
            <div class="my-4 block" v-if="authResponse?.errors?.length">
                <span
                    class="mb-1 block h-fit rounded bg-[#fdf1f5] px-[15px] py-1.5 leading-[30px] text-[#e47398] last:mb-0"
                    v-for="error in authResponse.errors"
                    v-test-id="'2fa-error-message'"
                >
                    {{ S[error.message] }}
                </span>
                <span v-html="S.support"></span>
            </div>
            <div>
                <label class="inline-flex cursor-pointer items-start gap-2">
                    <input
                        v-model="loginRecord.trustBrowser"
                        type="checkbox"
                        class="mt-5 h-6 w-6 shrink-0 pl-2 accent-[#1b1c41]"
                        v-test-id="'trust-browser'"
                    />
                    <span>{{ S.rememberThisBrowser }}</span>
                </label>
            </div>
        </div>

        <!--Forgot password  -->

        <div class="p-2" v-else-if="action == LoginAction.ForgotPassword">
            <h1 class="mb-5 text-3xl font-medium">{{ S.passwordSent }}</h1>
            <p class="mb-5 leading-[25px] text-[#1b1c41]">
                {{ S.forgotPasswordMessage }}
            </p>
            <input
                autofocus
                v-model="loginRecord.login"
                class="mt-2.5 box-border block h-11 w-full appearance-none rounded border-2 border-solid border-[#D5D6DE] bg-white px-3 py-2 text-base leading-3 text-[#1b1c41] outline-none focus:border-[#1b1c41]"
                :class="autofillInputFix"
                type="text"
                :placeholder="S.loginPlaceholder"
                v-test-id="'forgot-password-login'"
            />
            <div class="my-4 block" v-if="authResponse?.errors?.length">
                <span
                    class="mb-1 block h-fit rounded bg-[#fdf1f5] px-[15px] py-1.5 leading-[30px] text-[#e47398] last:mb-0"
                    v-for="error in authResponse.errors"
                    v-test-id="'forgot-password-error-message'"
                >
                    {{ S[error.message] }}
                </span>
            </div>
            <div class="mt-2.5 flex gap-4">
                <button
                    @click="
                        authResponse = null;
                        action = LoginAction.Login;
                    "
                    class="box-border flex h-11 min-h-[44px] flex-1 cursor-pointer items-center justify-center rounded border border-solid border-[#1b1c41] bg-white px-3 py-2 text-base leading-3 text-[#1b1c41] active:bg-gray-100"
                    v-test-id="'forgot-password-back'"
                >
                    {{ S.back }}
                </button>
                <button
                    :class="{
                        'pointer-events-none opacity-50': !loginRecord.login?.length
                    }"
                    @click="resetPassword"
                    class="box-border flex h-11 min-h-[44px] flex-1 cursor-pointer items-center justify-center rounded border-0 bg-[#1b1c41] px-3 py-2 text-base leading-3 text-white active:bg-[#252647]"
                    v-test-id="'reset-password'"
                >
                    {{ S.resetPassword }}
                </button>
            </div>
        </div>

        <!--Forgot password sent -->

        <div class="p-2" v-else-if="action == LoginAction.ForgotPasswordSent">
            <h1 class="mb-5 text-3xl font-medium">{{ S.passwordSent }}</h1>
            <p class="mb-5 leading-[25px] text-[#1b1c41]">{{ S.passwordSentMessage }}</p>
            <button
                @click="
                    authResponse = null;
                    action = LoginAction.Login;
                "
                class="box-border flex h-11 min-h-[44px] cursor-pointer items-center justify-center rounded border border-solid border-[#1b1c41] bg-white px-3 py-2 text-base leading-3 text-[#1b1c41] active:bg-gray-100"
                v-test-id="'password-sent-back'"
            >
                {{ S.back }}
            </button>
        </div>

        <!-- Reset password TODO -->

        <div class="p-2" v-else-if="action == LoginAction.ResetPassword">
            <div v-if="passwordRecord.login">
                <span>
                    {{ S.changePassword }}
                    <strong>{{ passwordRecord.login }}</strong>
                </span>
                <input
                    v-model="passwordRecord.newPassword"
                    autofocus
                    class="mt-2.5 box-border block h-11 w-full appearance-none rounded border-2 border-solid border-[#D5D6DE] bg-white px-3 py-2 text-base leading-3 text-[#1b1c41] outline-none focus:border-[#1b1c41]"
                    :class="autofillInputFix"
                    type="password"
                    :placeholder="S.newPassword"
                    v-test-id="'new-password'"
                />
                <input
                    name=""
                    v-model="passwordRecord.repeatNewPassword"
                    class="mt-2.5 box-border block h-11 w-full appearance-none rounded border-2 border-solid border-[#D5D6DE] bg-white px-3 py-2 text-base leading-3 text-[#1b1c41] outline-none focus:border-[#1b1c41]"
                    :class="autofillInputFix"
                    type="password"
                    :placeholder="S.newPasswordConfirm"
                    v-test-id="'new-password-repeat'"
                />
                <span class="my-3.5 inline-block leading-[25px] text-[#1b1c41]">{{ S.passwordRule }}</span>
                <div class="mb-4 block" v-if="authResponse?.errors?.length">
                    <span
                        class="mb-1 block h-fit rounded bg-[#fdf1f5] px-[15px] py-1.5 leading-[30px] text-[#e47398] last:mb-0"
                        v-for="error in authResponse.errors"
                        v-test-id="'new-password-error-message'"
                    >
                        {{ S[error.message] }}
                    </span>
                </div>
                <button
                    @click="changePassword"
                    class="mt-10 box-border flex h-11 min-h-[44px] w-full cursor-pointer items-center justify-center rounded border-0 bg-[#1b1c41] px-3 py-2 text-base leading-3 text-white active:bg-[#252647]"
                    :class="{
                        'pointer-events-none opacity-50':
                            !passwordRecord?.newPassword?.length ||
                            !passwordRecord?.repeatNewPassword?.length ||
                            passwordRecord?.repeatNewPassword != passwordRecord?.newPassword
                    }"
                    v-test-id="'change-password'"
                >
                    {{ S.changeAndLogin }}
                </button>
            </div>

            <!-- if reset password link is invalid -->
            <div v-if="!passwordRecord.login && authResponse?.errors?.length">
                <h3>{{ S.linkIsExpired }}</h3>
                <h4>
                    <a
                        @click="reload"
                        class="cursor-pointer text-[#0073ff] underline hover:text-[#0056c7]"
                        v-test-id="'reload'"
                    >
                        {{ S.linkToLogIn }}
                    </a>
                </h4>
            </div>
        </div>

        <div class="p-2" v-else-if="action == LoginAction.EvoMigration">
            <EvoMigrationPrompt
                :enforceEvoMigration="uiSettings.enforceEvoMigration"
                @skip="skipEvoMigration"
                @error="handleEvoMigrationError"
            />
        </div>
    </div>
</template>
